import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthState } from './hooks/useAuthState';
import './App.css';
import './styles/theme.css';
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import CreateEvent from './pages/CreateEvent';
import CreateGroup from './pages/CreateGroup';
import GroupView from './pages/GroupView';
import EventView from './pages/EventView';
import InviteResolver from './pages/InviteResolver';
import CheckIn from './pages/CheckIn';
import EventCheckInHost from './pages/EventCheckInHost';
import GroupSettings from './pages/GroupSettings';
import GroupCohosts from './pages/GroupCohosts';
import GroupMembers from './pages/GroupMembers';
import GroupPreview from './pages/GroupPreview';
import InvitesInbox from './pages/InvitesInbox';
import SponsorInvitesInbox from './pages/SponsorInvitesInbox';
import UserSettings from './pages/UserSettings';
import EditEvent from './pages/EditEvent';
import EventCohosts from './pages/EventCohosts';
import DeleteEvent from './pages/DeleteEvent';
import Contacts from './pages/Contacts';
import FriendRequestQR from './pages/FriendRequestQR';
import Support from './pages/Support';
import Hotelia2026 from './pages/Hotelia2026';
import ChatDrawer from './components/ChatDrawer';
import ProtectedRoute from './components/ProtectedRoute';
import TopNavBar from './components/TopNavBar';
import { db } from './firebase/config';

function App() {
  const { user, loading } = useAuthState();
  
  // Debug: Log auth state changes
  useEffect(() => {
    console.log('🔵 [App] Auth state changed:', { hasUser: !!user, loading, userId: user?.uid });
  }, [user, loading]);

  // Test Firebase connection on component mount
  useEffect(() => {
    console.log('Firebase Firestore initialized:', db);
  }, []);

  // Handle saving pending event after login
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const saveEvent = urlParams.get('saveEvent');
    
    if (saveEvent && user) {
      const pendingEvent = localStorage.getItem('pendingEvent');
      if (pendingEvent) {
        // Event will be saved in CreateEvent component
        // Clear the flag
        localStorage.removeItem('pendingEvent');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, [user]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

      return (
        <Router>
          <div className="App">
            <TopNavBar />
            <ChatDrawer />
            <Routes>
          <Route path="/login" element={!user ? <Login /> : <Navigate to="/home" />} />
          <Route path="/signup" element={!user ? <Signup /> : <Navigate to="/home" />} />
          <Route path="/create-event" element={<CreateEvent />} />
          <Route path="/create-group" element={user ? <CreateGroup /> : <Navigate to="/login" />} />
          <Route path="/groups/:groupId/cohosts" element={<ProtectedRoute user={user} loading={loading}><GroupCohosts /></ProtectedRoute>} />
          <Route path="/groups/:groupId/preview" element={<ProtectedRoute user={user} loading={loading}><GroupPreview /></ProtectedRoute>} />
          <Route path="/groups/:groupId/settings" element={<ProtectedRoute user={user} loading={loading}><GroupSettings /></ProtectedRoute>} />
          <Route path="/groups/:groupId/members" element={<ProtectedRoute user={user} loading={loading}><GroupMembers /></ProtectedRoute>} />
          <Route path="/groups/:groupId" element={<ProtectedRoute user={user} loading={loading}><GroupView /></ProtectedRoute>} />
          <Route path="/events/:eventId" element={<EventView />} />
          <Route path="/events/:eventId/edit" element={user ? <EditEvent /> : <Navigate to="/login" />} />
          <Route path="/events/:eventId/delete" element={<ProtectedRoute user={user} loading={loading}><DeleteEvent /></ProtectedRoute>} />
          <Route path="/events/:eventId/cohosts" element={user ? <EventCohosts /> : <Navigate to="/login" />} />
          <Route path="/events/:eventId/checkin" element={user ? <EventCheckInHost /> : <Navigate to="/login" />} />
          <Route path="/checkin/:eventId" element={<CheckIn />} />
          <Route path="/i/:token" element={<InviteResolver />} />
          <Route path="/inbox" element={user ? <InvitesInbox /> : <Navigate to="/login" />} />
          <Route path="/sponsor-invites" element={user ? <SponsorInvitesInbox /> : <Navigate to="/login" />} />
          <Route path="/contacts" element={user ? <Contacts /> : <Navigate to="/login" />} />
          <Route path="/friend-request/:userId" element={<FriendRequestQR />} />
          <Route path="/settings" element={user ? <UserSettings /> : <Navigate to="/login" />} />
          <Route path="/support" element={<Support />} />
          <Route path="/hotelia2026" element={<Hotelia2026 />} />
          <Route path="/home" element={user ? <Home /> : <Navigate to="/login" />} />
          <Route path="/" element={<Navigate to={user ? "/home" : "/login"} />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
